import {
  useScroll,
  useMotionValue,
  useSpring,
  useTransform,
  clamp,
  Transition,
  MotionProps,
  motion,
  AnimatePresence,
} from "framer-motion";
import React from "react";
import {useEffect, useState} from "react";
import {Button} from "../components";
import {useMedia} from "../hooks/useMedia";
import {cn, spring} from "../lib/utils";
import {Dropdown} from "./Dropdown";
import * as Icon from "../icons";
import * as Dialog from "@radix-ui/react-dialog";
import {useTranslation} from "react-i18next";

const Nav = ({
  logoUrl,
  customerName = null,
  hideNav = false,
}: {
  logoUrl: string;
  customerName?: string;
  hideNav?: boolean;
}) => {
  const {t} = useTranslation();

  const isLoggedIn = !!customerName;
  const MENU_ITEMS = window.navigationLinks
    .filter((_, i) => (i === 0 ? isLoggedIn : true));

  const [menuOpen, setMenuOpen] = useState(false);
  const isMobile = useMedia("(max-width: 768px)", true);
  const showMenu = menuOpen && isMobile;

  const {scrollY} = useScroll();
  const lastY = useMotionValue(0);
  const yTarget = useMotionValue(0);
  const y = useSpring(yTarget, spring.smooth);

  const opacity = useTransform(scrollY, [0, 1], [0, 1], {clamp: true});

  useEffect(() => {
    let id: ReturnType<typeof setTimeout>;

    scrollY.on("change", (v) => {
      if (v < 0) return;
      const _y = yTarget.get();
      const diff = v - lastY.get();
      const offsetY = clamp(-64, 0, _y - diff);
      yTarget.set(offsetY);
      lastY.set(v);

      if (id) clearTimeout(id);
      id = setTimeout(() => {
        if (_y < 0 && _y > -64 && scrollY.get() > 64) {
          if (diff < 0) {
            console.log(_y);
            yTarget.set(0);
          } else {
            yTarget.set(-64);
          }
        }
      }, 500);
    });
  }, []);

  const transitionAppear: Transition = {
    ...spring.smooth,
    staggerChildren: 0.035,
  };
  const parentAnimations: Pick<
    MotionProps,
    "transition" | "initial" | "animate" | "exit"
  > = {
    initial: "initial",
    animate: "animate",
    exit: "exit",
    transition: {
      ...transitionAppear,
      opacity: {duration: 0.1, ease: "linear"},
    },
  };
  const childAnimations: Pick<MotionProps, "transition" | "variants"> = {
    transition: transitionAppear,
    variants: {
      initial: {opacity: 0, y: -20},
      animate: {opacity: 1, y: 0},
      exit: {opacity: 0},
    },
  };

  return (
    <>
      <motion.div
        style={{y}}
        className={cn([
          "sticky top-0 bg-white z-40 shadow-[0_-10px_0_white] h-16",
          "md:border-b-[0.5px] border-black/[0.1]",
        ])}
      >
        <motion.div
          className="flex flex-col h-full w-full max-w-7xl mx-auto px-4 bg-white rounded-3xl overflow-hidden"
          transition={spring.smooth}
          animate={{
            height: showMenu ? "55vh" : "100%",
          }}
        >
          <div className="flex justify-between items-center h-16 flex-shrink-0">
            <a href="/" className="lg:pr-20">
              <motion.img
                transition={spring.smooth}
                animate={{x: !showMenu ? 0 : 6}}
                src={logoUrl}
                width="80"
                height="48"
              />
            </a>
            {!hideNav && (
              <>
                <div className="max-md:hidden flex gap-4 lg:gap-6">
                  {MENU_ITEMS.map(({title, href}) => (
                    <a
                      key={title}
                      {...childAnimations}
                      className="text-sm font-semibold uppercase tracking-wide leading-10 text-green-900 hover:text-green-600"
                      href={href}
                    >
                      {title.replace(/ /g, '\u00A0')}
                    </a>
                  ))}
                </div>

                {!isLoggedIn ? (
                  <Button
                    variant="ghost"
                    className="max-md:hidden !rounded-full !h-11 px-8"
                    asChild
                  >
                    <a href={t("recipe_details.navigation_menu_to_be_removed.login.href")}>
                      {t("recipe_details.navigation_menu_to_be_removed.login.title")}
                    </a>
                  </Button>
                ) : (
                  <Dropdown>
                    <Button
                      variant="soft"
                      className="max-md:hidden text-md !rounded-full !h-11 pl-6 pr-[2px] gap-4 group"
                    >
                      {customerName}
                      <div className="bg-white rounded-full p-[10px] group-hover:bg-white/80 group-active:bg-white/20">
                        <Icon.BurgerMenu className="w-5 h-5" />
                      </div>
                    </Button>
                  </Dropdown>
                )}
                <div
                  className="md:hidden grid place-items-center -mr-2"
                  onClick={() => setMenuOpen(!menuOpen)}
                >
                  <AnimatePresence initial={false}>
                    <Button
                      variant="soft"
                      className={cn([
                        "text-md !rounded-full !h-11 pl-6 pr-[2px] gap-2 group",
                        (showMenu || !isLoggedIn) && "!bg-transparent",
                      ])}
                    >
                      <span className={cn(showMenu && "opacity-0")}>
                        {customerName}
                      </span>
                      {!showMenu ? (
                        <motion.div
                          key="menu"
                          className="[grid-area:1/1] bg-white rounded-full"
                          transition={transitionAppear}
                          initial={{rotate: -90, opacity: 0, scale: 0.5}}
                          animate={{rotate: 0, opacity: 1, scale: 1}}
                          exit={{rotate: -90, opacity: 0, scale: 0.5}}
                        >
                          <Icon.BurgerMenu className="w-5 h-5 m-2" />
                        </motion.div>
                      ) : (
                        <motion.div
                          key="close"
                          className="[grid-area:1/1]"
                          transition={transitionAppear}
                          initial={{rotate: 90, opacity: 0, scale: 0.5}}
                          animate={{rotate: 0, opacity: 1, scale: 1}}
                          exit={{rotate: 90, opacity: 0, scale: 0.5}}
                        >
                          <Icon.Close className="stroke-green-900 m-3" />
                        </motion.div>
                      )}
                    </Button>
                  </AnimatePresence>
                </div>
              </>
            )}
          </div>
          {!hideNav && (
            <>
              <AnimatePresence>
                {showMenu && (
                  <motion.div
                    className="flex flex-col justify-end text-xl gap-4 min-h-[calc(55vh-64px)]"
                    exit={{y: -20}}
                  >
                    <motion.div
                      {...parentAnimations}
                      className="flex flex-col gap px-2 flex-1 justify-center"
                    >
                      {MENU_ITEMS.map(({title, href}) => (
                        <motion.a
                          key={title}
                          {...childAnimations}
                          className="text-base font-semibold uppercase tracking-wide leading-10 text-green-900 hover:text-green-600"
                          href={href}
                        >
                          {title}
                        </motion.a>
                      ))}
                    </motion.div>
                    <motion.div
                      className="flex gap-2 w-full pb-6"
                      {...parentAnimations}
                      transition={{
                        ...parentAnimations.transition,
                        delayChildren: 0.15,
                      }}
                    >
                      {!isLoggedIn && (
                        <>
                          <Button
                            variant="ghost"
                            className="flex-1 rounded-full"
                            asChild
                          >
                            <motion.a
                              {...childAnimations}
                              href={t("recipe_details.navigation_menu_to_be_removed.login.href")}
                            >
                              {t("recipe_details.navigation_menu_to_be_removed.login.title")}
                            </motion.a>
                          </Button>
                          <Button className="flex-1 rounded-full" asChild>
                            <motion.a
                              {...childAnimations}
                              href="/account/register"
                            >
                              Registreeru
                            </motion.a>
                          </Button>
                        </>
                      )}
                      {isLoggedIn && (
                        <Button
                          variant="ghost"
                          className="flex-1 rounded-full"
                          asChild
                        >
                          <motion.a {...childAnimations} href="/account/logout">
                            Logi välja
                          </motion.a>
                        </Button>
                      )}
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}
        </motion.div>
        <motion.div
          style={{opacity}}
          transition={{duration: 0.1}}
          className={cn([
            "md:hidden h-[0.5px] left-2 w-[calc(100vw-16px)] bg-black/[0.1] sticky top-0 z-10",
            //"before:content-[''] before:h-4 before:bg-gradient-to-b from-white to-transparent before:w-full before:block",
          ])}
        />
      </motion.div>
      {!hideNav && (
        <>
          <Dialog.Root open={showMenu}>
            <AnimatePresence>
              {showMenu && (
                <Dialog.DialogOverlay forceMount asChild>
                  <motion.div
                    onTap={() => setMenuOpen(false)}
                    className="bg-zinc-300/80 fixed inset-0 backdrop-blur-md z-30"
                    transition={{duration: 0.2, ease: "linear"}}
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    exit={{opacity: 0}}
                  />
                </Dialog.DialogOverlay>
              )}
            </AnimatePresence>
          </Dialog.Root>
        </>
      )}
    </>
  );
};

export {Nav};
